import { useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { KeyRound, Copy, CheckCircle2, Loader2, AlertTriangle, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";

/**
 * Reusable admin action — regenerates a temporary password for a passenger,
 * driver, or partner and displays it once in a modal (with copy button and
 * WhatsApp share shortcut). Only visible to admins who hold the `manage_users`
 * permission (super-admins have it implicitly).
 *
 * Props:
 *  - user: { id, name, email, phone, role }
 *  - variant?: "button" | "icon"  (default "button")
 *  - className?: string           (extra classes for the trigger)
 *  - testIdPrefix?: string        (fallback = "admin-reset-password")
 */
export default function AdminResetPasswordButton({ user, variant = "button", className = "", testIdPrefix = "admin-reset-password" }) {
  const { user: currentAdmin } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const canReset =
    currentAdmin?.role === "admin" &&
    (currentAdmin?.is_super_admin === true ||
      (currentAdmin?.admin_permissions || []).includes("manage_users"));

  if (!canReset) return null;
  if (!user || user.role === "admin") return null;

  const testId = `${testIdPrefix}-${user.id}`;

  const doReset = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/reset-password`);
      setResult(data);
      setConfirmOpen(false);
      setResultOpen(true);
      setCopied(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = async () => {
    if (!result?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(result.temporary_password);
      setCopied(true);
      toast.success("Mot de passe copié");
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {
      toast.error("Impossible de copier — sélectionnez et copiez manuellement");
    }
  };

  const whatsappLink = () => {
    if (!result) return null;
    const phone = (result.user?.phone || "").replace(/[^0-9]/g, "");
    if (!phone) return null;
    const text = encodeURIComponent(
      `Bonjour ${result.user?.name || ""},\n\nVotre mot de passe TrigoTour a été réinitialisé.\n\nMot de passe temporaire : ${result.temporary_password}\n\nMerci de le modifier dès votre prochaine connexion.`
    );
    return `https://wa.me/${phone}?text=${text}`;
  };

  const triggerBtn =
    variant === "icon" ? (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setConfirmOpen(true)}
        className={`h-7 px-2 text-xs text-[#0A2540] hover:bg-[#FACC15]/20 ${className}`}
        title="Réinitialiser mot de passe"
        data-testid={testId}
      >
        <KeyRound className="w-3.5 h-3.5" />
      </Button>
    ) : (
      <Button
        size="sm"
        variant="outline"
        onClick={() => setConfirmOpen(true)}
        className={`text-xs border-[#FACC15] text-[#0A2540] hover:bg-[#FACC15]/20 h-7 ${className}`}
        data-testid={testId}
      >
        <KeyRound className="w-3 h-3 mr-0.5" /> Mot de passe
      </Button>
    );

  return (
    <>
      {triggerBtn}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !o && !loading && setConfirmOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid={`${testId}-confirm-dialog`}>
          <DialogHeader>
            <DialogTitle className="text-[#0A2540] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <KeyRound className="w-5 h-5 text-[#FACC15]" />
              Réinitialiser le mot de passe
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Un mot de passe temporaire aléatoire sera généré pour{" "}
              <strong className="text-[#0A2540]">{user.name}</strong> ({user.email}).
              L&apos;ancien mot de passe cessera immédiatement de fonctionner.
              Vous verrez ce mot de passe <u>une seule fois</u>.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-[#FEF3C7] border border-[#FACC15]/50 p-3 text-[11px] text-[#92400E] flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Communiquez le mot de passe temporaire à l&apos;utilisateur par un canal privé
              (WhatsApp, appel, SMS). Invitez-le à le modifier dès sa prochaine connexion.
            </span>
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={loading}
              className="flex-1 border-[#CBD5E1]"
              data-testid={`${testId}-cancel`}
            >
              Annuler
            </Button>
            <Button
              onClick={doReset}
              disabled={loading}
              className="flex-1 bg-[#0A2540] text-white hover:bg-[#081b2e]"
              data-testid={`${testId}-confirm`}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <KeyRound className="w-4 h-4 mr-1" />
              )}
              Générer un mot de passe
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Result dialog */}
      <Dialog open={resultOpen} onOpenChange={(o) => !o && setResultOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid={`${testId}-result-dialog`}>
          <DialogHeader>
            <DialogTitle className="text-[#10B981] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <CheckCircle2 className="w-5 h-5" />
              Mot de passe généré
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Pour <strong className="text-[#0A2540]">{result?.user?.name}</strong> ({result?.user?.email})
            </DialogDescription>
          </DialogHeader>

          <div>
            <label className="block text-[11px] font-medium text-[#64748B] mb-1 uppercase tracking-wide">
              Mot de passe temporaire
            </label>
            <div
              className="flex items-center gap-2 bg-[#0A2540] rounded-lg p-3"
              data-testid={`${testId}-password-box`}
            >
              <span
                className="flex-1 font-mono text-lg text-[#FACC15] tracking-wider break-all select-all"
                data-testid={`${testId}-password-value`}
              >
                {result?.temporary_password}
              </span>
              <Button
                size="sm"
                onClick={copyPassword}
                className="shrink-0 bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308]"
                data-testid={`${testId}-copy`}
              >
                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[10px] text-[#EF4444] mt-1.5">
              ⚠ Ce mot de passe ne sera plus jamais affiché. Sauvegardez-le maintenant.
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            {whatsappLink() && (
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-1 h-9 rounded-md bg-[#25D366] text-white text-sm font-medium hover:bg-[#1FB855] transition"
                data-testid={`${testId}-whatsapp`}
              >
                <MessageCircle className="w-4 h-4" /> Envoyer via WhatsApp
              </a>
            )}
            <Button
              onClick={() => setResultOpen(false)}
              className={`${whatsappLink() ? "" : "flex-1 "}bg-[#0A2540] text-white hover:bg-[#081b2e]`}
              data-testid={`${testId}-close`}
            >
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
